import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Star, MapPin, Clock, MessageCircle, Video, Home, Building2, ChevronRight, Share2, Heart } from 'lucide-react';
import { professionals } from '../data/mockData';
import { useAppContext } from '../contexts/AppContext';
export function ProfessionalProfileScreen() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    setSelectedProfessional
  } = useAppContext();
  const prof = professionals.find(p => p.id === id) || professionals[0];
  const handleBookNow = () => {
    setSelectedProfessional(prof);
    navigate('/booking/step1');
  };
  const modeIcons: Record<string, React.ElementType> = {
    Clinic: Building2,
    'Home Visit': Home,
    Video: Video,
    Chat: MessageCircle
  };
  return <div className="min-h-screen bg-ui-bg pb-24" data-id="element-1217">
      {/* Header Image & Actions */}
      <div className="relative h-64 bg-brand-light" data-id="element-1218">
        <img src={prof.image} alt={prof.name} className="w-full h-full object-cover" data-id="element-1219" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-1220"></div>

        <div className="absolute top-12 left-0 right-0 px-6 flex justify-between items-center" data-id="element-1221">
          <button onClick={() => navigate(-1)} className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white" data-id="element-1222">
            <ArrowLeft size={20} data-id="element-1223" />
          </button>
          <div className="flex space-x-3" data-id="element-1224">
            <button className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white" data-id="element-1225">
              <Share2 size={20} data-id="element-1226" />
            </button>
            <button className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white" data-id="element-1227">
              <Heart size={20} data-id="element-1228" />
            </button>
          </div>
        </div>
      </div>

      {/* Profile Info */}
      <div className="px-6 -mt-6 relative z-10" data-id="element-1229">
        <div className="bg-white rounded-2xl p-5 shadow-card border border-ui-border" data-id="element-1230">
          <div className="flex justify-between items-start mb-2" data-id="element-1231">
            <div data-id="element-1232">
              <div className="flex items-center" data-id="element-1233">
                <h1 className="text-xl font-bold text-text-main" data-id="element-1234">
                  {prof.name}
                </h1>
                {prof.verified && <ShieldCheck size={20} className="text-brand-primary ml-1.5" data-id="element-1235" />}
              </div>
              <p className="text-text-secondary font-medium" data-id="element-1236">
                {prof.title} • {prof.specialty}
              </p>
            </div>
          </div>

          <div className="flex items-center mt-4 space-x-4" data-id="element-1237">
            <div className="flex flex-col items-center p-2 bg-ui-card rounded-xl flex-1" data-id="element-1238">
              <div className="flex items-center text-status-warning font-bold" data-id="element-1239">
                <Star size={16} className="fill-current mr-1" data-id="element-1240" /> {prof.rating}
              </div>
              <span className="text-[10px] text-text-secondary mt-1" data-id="element-1241">
                {prof.reviewsCount} Reviews
              </span>
            </div>
            <div className="flex flex-col items-center p-2 bg-ui-card rounded-xl flex-1" data-id="element-1242">
              <span className="font-bold text-text-main" data-id="element-1243">
                {prof.experience} yrs
              </span>
              <span className="text-[10px] text-text-secondary mt-1" data-id="element-1244">
                Experience
              </span>
            </div>
            <div className="flex flex-col items-center p-2 bg-ui-card rounded-xl flex-1" data-id="element-1245">
              <span className="font-bold text-text-main" data-id="element-1246">100%</span>
              <span className="text-[10px] text-text-secondary mt-1" data-id="element-1247">
                Response
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 mt-6 space-y-6" data-id="element-1248">
        {/* Consultation Modes */}
        <div data-id="element-1249">
          <h3 className="font-bold text-text-main mb-3" data-id="element-1250">Available For</h3>
          <div className="flex flex-wrap gap-2" data-id="element-1251">
            {prof.modes.map(mode => {
            const Icon = modeIcons[mode];
            return <div key={mode} className="flex items-center bg-brand-light text-brand-primary px-3 py-2 rounded-lg text-sm font-medium" data-id="element-1252">
                  {Icon && <Icon size={16} className="mr-2" data-id="element-1253" />}
                  {mode}
                </div>;
          })}
          </div>
        </div>

        {/* About */}
        <div data-id="element-1254">
          <h3 className="font-bold text-text-main mb-2" data-id="element-1255">About</h3>
          <p className="text-text-secondary text-sm leading-relaxed" data-id="element-1256">
            {prof.bio}
          </p>
        </div>

        {/* Details */}
        <div className="space-y-4 bg-ui-card p-4 rounded-2xl" data-id="element-1257">
          <div className="flex items-start" data-id="element-1258">
            <Building2 size={20} className="text-text-secondary mr-3 shrink-0 mt-0.5" data-id="element-1259" />
            <div data-id="element-1260">
              <p className="text-sm font-medium text-text-main" data-id="element-1261">Affiliation</p>
              <p className="text-sm text-text-secondary" data-id="element-1262">{prof.affiliation}</p>
            </div>
          </div>
          <div className="flex items-start" data-id="element-1263">
            <MapPin size={20} className="text-text-secondary mr-3 shrink-0 mt-0.5" data-id="element-1264" />
            <div data-id="element-1265">
              <p className="text-sm font-medium text-text-main" data-id="element-1266">Location</p>
              <p className="text-sm text-text-secondary" data-id="element-1267">
                {prof.distance} away
              </p>
            </div>
          </div>
          <div className="flex items-start" data-id="element-1268">
            <MessageCircle size={20} className="text-text-secondary mr-3 shrink-0 mt-0.5" data-id="element-1269" />
            <div data-id="element-1270">
              <p className="text-sm font-medium text-text-main" data-id="element-1271">Languages</p>
              <p className="text-sm text-text-secondary" data-id="element-1272">
                {prof.languages.join(', ')}
              </p>
            </div>
          </div>
        </div>

        {/* Services */}
        <div data-id="element-1273">
          <h3 className="font-bold text-text-main mb-3" data-id="element-1274">Services Offered</h3>
          <div className="space-y-2" data-id="element-1275">
            {prof.services.map((service, idx) => <div key={idx} className="flex items-center text-sm text-text-secondary" data-id="element-1276">
                <div className="w-1.5 h-1.5 rounded-full bg-brand-primary mr-2" data-id="element-1277"></div>
                {service}
              </div>)}
          </div>
        </div>

        {/* Reviews Preview */}
        <div data-id="element-1278">
          <div className="flex justify-between items-center mb-3" data-id="element-1279">
            <h3 className="font-bold text-text-main" data-id="element-1280">Patient Reviews</h3>
            <button className="text-brand-primary text-sm font-medium" data-id="element-1281">
              See all
            </button>
          </div>
          <div className="bg-white border border-ui-border p-4 rounded-2xl shadow-sm" data-id="element-1282">
            <div className="flex justify-between items-start mb-2" data-id="element-1283">
              <div className="flex items-center" data-id="element-1284">
                <div className="w-8 h-8 bg-gray-200 rounded-full mr-2" data-id="element-1285"></div>
                <div data-id="element-1286">
                  <p className="text-sm font-bold" data-id="element-1287">Aisha M.</p>
                  <p className="text-[10px] text-text-secondary" data-id="element-1288">2 weeks ago</p>
                </div>
              </div>
              <div className="flex text-status-warning" data-id="element-1289">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} size={12} className="fill-current" data-id="element-1290" />)}
              </div>
            </div>
            <p className="text-sm text-text-secondary" data-id="element-1291">
              Very professional and attentive. Took the time to explain my
              condition clearly. Highly recommended!
            </p>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 w-full max-w-[430px] mx-auto bg-white border-t border-ui-border p-4 pb-safe z-50 flex items-center justify-between shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.1)]" data-id="element-1292">
        <div className="flex flex-col" data-id="element-1293">
          <span className="text-xs text-text-secondary" data-id="element-1294">Consultation Fee</span>
          <span className="text-xl font-bold text-brand-primary" data-id="element-1295">
            ₦{prof.fee.toLocaleString()}
          </span>
        </div>
        <div className="flex space-x-3" data-id="element-1296">
          <button className="px-4 py-3 border border-brand-primary text-brand-primary rounded-xl font-semibold active:bg-brand-light transition-colors" data-id="element-1297">
            Chat
          </button>
          <button onClick={handleBookNow} className="px-8 py-3 bg-brand-primary text-white rounded-xl font-semibold shadow-md active:scale-[0.98] transition-transform" data-id="element-1298">
            Book Now
          </button>
        </div>
      </div>
    </div>;
}